/**
 * Todo Sync Service
 * Handles external synchronization (Google Sheets, etc.)
 * Part of the modularized todo service architecture
 */

import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from '../service-registry';
import { typedSupabase } from '@/lib/typed-supabase';
import { Todo } from '@/types/todo';
import type { Database } from '@flowstate/types/database';
import { logger } from '@/lib/logger';

export interface SyncResult {
  success: boolean;
  syncedCount: number;
  errors: string[];
  timestamp: string;
}

export interface GoogleSheetsSyncConfig {
  spreadsheetId: string;
  sheetName: string;
  columnMapping: {
    title: string;
    description?: string;
    status?: string;
    priority?: string;
    due_date?: string;
    assigned_to?: string;
  };
}

export interface ITodoSyncService extends IService {
  syncWithGoogleSheets(config?: GoogleSheetsSyncConfig): Promise<ServiceResponse<SyncResult>>;
  exportToGoogleSheets(todos: Todo[], config: GoogleSheetsSyncConfig): Promise<ServiceResponse<SyncResult>>;
  importFromGoogleSheets(config: GoogleSheetsSyncConfig): Promise<ServiceResponse<Todo[]>>;
  syncWithExternalSystem(systemName: string, config: Record<string, unknown>): Promise<ServiceResponse<SyncResult>>;
  getSyncHistory(limit?: number): Promise<ServiceResponse<SyncResult[]>>;
  validateSyncConfig(systemName: string, config: GoogleSheetsSyncConfig | Record<string, unknown>): Promise<ServiceResponse<boolean>>;
}

export class TodoSyncService implements ITodoSyncService {
  readonly name = 'TodoSyncService';
  private initialized = false;

  async initialize(): Promise<void> {
    this.initialized = true;
    logger.info('TodoSyncService initialized');
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info('TodoSyncService disposed');
  }

  async syncWithGoogleSheets(config?: GoogleSheetsSyncConfig): Promise<ServiceResponse<SyncResult>> {
    try {
      logger.debug('Starting Google Sheets sync');

      if (!config) {
        // Use default configuration from environment or database
        const defaultConfig = await this.getDefaultGoogleSheetsConfig();
        if (!defaultConfig.success) {
          return createErrorResponse(defaultConfig.error);
        }
        config = defaultConfig.data!;
      }

      // Validate configuration
      const validation = await this.validateSyncConfig('google-sheets', config);
      if (!validation.success || !validation.data) {
        return createErrorResponse('Invalid Google Sheets configuration');
      }

      const result: SyncResult = {
        success: true,
        syncedCount: 0,
        errors: [],
        timestamp: new Date().toISOString(),
      };

      // Get todos to sync (those marked for sync or all if full sync)
      const todosToSync = await this.getTodosForSync();
      if (!todosToSync.success) {
        return createErrorResponse('Failed to get todos for sync');
      }

      // Export to Google Sheets
      const exportResult = await this.exportToGoogleSheets(todosToSync.data!, config);
      if (!exportResult.success) {
        result.success = false;
        result.errors.push('Export failed: ' + exportResult.error);
      } else {
        result.syncedCount = exportResult.data!.syncedCount;
      }

      // Log sync result
      await this.logSyncResult('google-sheets', result);

      logger.info('Google Sheets sync completed', {
        syncedCount: result.syncedCount,
        errors: result.errors.length,
        success: result.success
      });

      return createSuccessResponse(result);

    } catch (error) {
      logger.error('Error in syncWithGoogleSheets', {
        error: error instanceof Error ? error.message : error
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Google Sheets sync failed');
    }
  }

  async exportToGoogleSheets(todos: Todo[], config: GoogleSheetsSyncConfig): Promise<ServiceResponse<SyncResult>> {
    try {
      logger.debug('Exporting todos to Google Sheets', { count: todos.length, spreadsheetId: config.spreadsheetId });

      const result: SyncResult = {
        success: true,
        syncedCount: 0,
        errors: [],
        timestamp: new Date().toISOString(),
      };

      // Prepare data for Google Sheets format
      const sheetData = this.prepareSheetData(todos, config);

      // Call Supabase Edge Function for Google Sheets integration
      const { data, error } = await typedSupabase.functions.invoke('google-sheets-sync', {
        body: {
          action: 'export',
          spreadsheetId: config.spreadsheetId,
          sheetName: config.sheetName,
          data: sheetData,
          config: config.columnMapping,
        },
      });

      if (error) {
        logger.error('Google Sheets export failed', { error: error.message });
        result.success = false;
        result.errors.push(error.message);
        return createSuccessResponse(result);
      }

      result.syncedCount = data?.syncedCount ?? todos.length;
      result.success = data?.success !== false;

      if (data?.errors?.length > 0) {
        result.errors.push(...data.errors);
      }

      logger.info('Todos exported to Google Sheets', {
        spreadsheetId: config.spreadsheetId,
        syncedCount: result.syncedCount,
        errors: result.errors.length
      });

      return createSuccessResponse(result);

    } catch (error) {
      logger.error('Error in exportToGoogleSheets', {
        error: error instanceof Error ? error.message : error,
        todoCount: todos.length
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Export to Google Sheets failed');
    }
  }

  async importFromGoogleSheets(config: GoogleSheetsSyncConfig): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Importing todos from Google Sheets', { spreadsheetId: config.spreadsheetId });

      // Call Supabase Edge Function for Google Sheets integration
      const { data, error } = await typedSupabase.functions.invoke('google-sheets-sync', {
        body: {
          action: 'import',
          spreadsheetId: config.spreadsheetId,
          sheetName: config.sheetName,
          config: config.columnMapping,
        },
      });

      if (error) {
        logger.error('Google Sheets import failed', { error: error.message });
        return createErrorResponse('Import from Google Sheets failed: ' + error.message);
      }

      if (!data?.rows || !Array.isArray(data.rows)) {
        return createErrorResponse('Invalid response from Google Sheets import');
      }

      // Convert sheet rows to Todo objects
      const importedTodos: Todo[] = [];
      for (const row of data.rows) {
        try {
          const todo = this.convertSheetRowToTodo(row, config);
          importedTodos.push(todo);
        } catch (conversionError) {
          logger.warn('Failed to convert sheet row to todo', {
            row,
            error: conversionError instanceof Error ? conversionError.message : conversionError
          });
        }
      }

      logger.info('Todos imported from Google Sheets', {
        spreadsheetId: config.spreadsheetId,
        importedCount: importedTodos.length,
        totalRows: data.rows.length
      });

      return createSuccessResponse(importedTodos);

    } catch (error) {
      logger.error('Error in importFromGoogleSheets', {
        error: error instanceof Error ? error.message : error,
        spreadsheetId: config.spreadsheetId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Import from Google Sheets failed');
    }
  }

  async syncWithExternalSystem(systemName: string, config: Record<string, unknown>): Promise<ServiceResponse<SyncResult>> {
    try {
      logger.debug('Syncing with external system', { systemName });

      // Validate configuration
      const validation = await this.validateSyncConfig(systemName, config);
      if (!validation.success || !validation.data) {
        return createErrorResponse(`Invalid configuration for ${systemName}`);
      }

      const result: SyncResult = {
        success: true,
        syncedCount: 0,
        errors: [],
        timestamp: new Date().toISOString(),
      };

      // Route to appropriate sync method based on system
      switch (systemName.toLowerCase()) {
        case 'google-sheets':
          return this.syncWithGoogleSheets(config as unknown as GoogleSheetsSyncConfig);

        case 'notion':
          // Future: implement Notion sync
          result.errors.push('Notion sync not yet implemented');
          result.success = false;
          break;

        case 'asana':
          // Future: implement Asana sync
          result.errors.push('Asana sync not yet implemented');
          result.success = false;
          break;

        default:
          result.errors.push(`Unsupported sync system: ${systemName}`);
          result.success = false;
      }

      // Log sync result
      await this.logSyncResult(systemName, result);

      return createSuccessResponse(result);

    } catch (error) {
      logger.error('Error in syncWithExternalSystem', {
        error: error instanceof Error ? error.message : error,
        systemName
      });
      return createErrorResponse(error instanceof Error ? error.message : 'External system sync failed');
    }
  }

  async getSyncHistory(limit: number = 50): Promise<ServiceResponse<SyncResult[]>> {
    try {
      logger.debug('Getting sync history', { limit });

      // This would query a sync_history table
      // For now, return empty array as placeholder
      const { data, error } = await typedSupabase
        .from('sync_history')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) {
        // If table doesn't exist yet, return empty array
        if (error.code === '42P01') { // relation does not exist
          logger.debug('Sync history table does not exist yet');
          return createSuccessResponse([]);
        }
        logger.error('Failed to get sync history', { error: error.message });
        throw error;
      }

      logger.debug('Sync history retrieved', { count: data.length });
      return createSuccessResponse((data ?? []) as unknown as SyncResult[]);

    } catch (error) {
      logger.error('Error in getSyncHistory', {
        error: error instanceof Error ? error.message : error,
        limit
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get sync history failed');
    }
  }

  async validateSyncConfig(systemName: string, config: GoogleSheetsSyncConfig | Record<string, unknown>): Promise<ServiceResponse<boolean>> {
    try {
      logger.debug('Validating sync config', { systemName });

      let isValid = false;

      switch (systemName.toLowerCase()) {
        case 'google-sheets': {
          const gsConfig = config as unknown as GoogleSheetsSyncConfig;
          isValid = !!(
            gsConfig.spreadsheetId &&
            gsConfig.sheetName &&
            gsConfig.columnMapping &&
            gsConfig.columnMapping.title
          );
          break;
        }

        default:
          isValid = false;
      }

      logger.debug('Sync config validation result', { systemName, isValid });
      return createSuccessResponse(isValid);

    } catch (error) {
      logger.error('Error in validateSyncConfig', {
        error: error instanceof Error ? error.message : error,
        systemName
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Validate sync config failed');
    }
  }

  // Private helper methods

  private async getDefaultGoogleSheetsConfig(): Promise<ServiceResponse<GoogleSheetsSyncConfig>> {
    // This would fetch from database or environment variables
    // For now, return a placeholder error
    return createErrorResponse('Default Google Sheets config not implemented');
  }

  private async getTodosForSync(): Promise<ServiceResponse<Todo[]>> {
    // Get todos that should be synced (updated recently)
    const { data, error } = await typedSupabase
      .from('todos')
      .select('*')
      .order('updated_at', { ascending: false });

    if (error) {
      logger.error('Failed to get todos for sync', { error: error.message });
      return createErrorResponse('Failed to get todos for sync');
    }

    return createSuccessResponse(data as Todo[]);
  }

  private prepareSheetData(todos: Todo[], config: GoogleSheetsSyncConfig): unknown[][] {
    const headers = [
      config.columnMapping.title,
      config.columnMapping.description ?? 'Description',
      config.columnMapping.status ?? 'Status',
      config.columnMapping.priority ?? 'Priority',
      config.columnMapping.due_date ?? 'Due Date',
      config.columnMapping.assigned_to ?? 'Assigned To',
    ];

    const rows = todos.map(todo => [
      todo.title,
      todo.description ?? '',
      todo.status,
      todo.priority || 'medium',
      todo.due_date ?? '',
      todo.assigned_to ?? '',
    ]);

    return [headers, ...rows];
  }

  private convertSheetRowToTodo(row: unknown[], _config: GoogleSheetsSyncConfig): Todo {
    // This would map sheet columns back to Todo object
    // Implementation depends on the exact sheet structure
    return {
      id: `imported-${Date.now()}-${Math.random()}`,
      title: typeof row[0] === 'string' ? row[0] : 'Imported Todo',
      description: typeof row[1] === 'string' ? row[1] : '',
      status:
        row[2] === 'todo' || row[2] === 'in_progress' || row[2] === 'completed'
          ? row[2]
          : 'todo',
      priority:
        row[3] === 'low' || row[3] === 'medium' || row[3] === 'high'
          ? row[3]
          : 'medium',
      due_date: typeof row[4] === 'string' ? row[4] : null,
      assigned_to: typeof row[5] === 'string' ? row[5] : null,
      organization_id: null,
      parent_id: null,
      created_by: 'system',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as unknown as Todo;
  }

  private async logSyncResult(systemName: string, result: SyncResult): Promise<void> {
    try {
      // This would insert into a sync_history table
      await typedSupabase
        .from('sync_history')
        .insert({
          system_name: systemName,
          result: result as unknown as Database['public']['Tables']['sync_history']['Insert']['result'],
          created_at: new Date().toISOString(),
        });
    } catch (error) {
      logger.warn('Failed to log sync result', {
        error: error instanceof Error ? error.message : error,
        systemName
      });
      // Don't fail the sync for logging issues
    }
  }
}

// Export singleton instance
export const todoSyncService = new TodoSyncService();
